import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Modal, Form, Input, InputNumber, Select, AutoComplete, Row, Col, useApp, Switch, Tabs, Button, Table, Tag, Alert, Space, Divider, Tooltip,
} from '../../ui';
import { Trash2, Plus, Lightbulb } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useConfirmedMutation } from '../../lib/confirm';
import { request } from '../../api/client';
import { requestAllPages } from '../../api/allPages';
import { usePermission } from '../../store/auth';
import { FileUploadField } from '../../components/FileUploadField';
import { MultiImageUploadField } from '../../components/MultiImageUploadField';
import { MultiFileUploadField } from '../../components/MultiFileUploadField';
import { ProductDetailsEditor, ProductDetailsState } from './ProductDetailsEditor';
import { toImageUrls } from '../../lib/imageUrls';
import { fmtUom } from '../../lib/format';
import { PALETTE } from '../../lib/palette';

interface Props {
  open: boolean;
  onClose: () => void;
  record?: any;
}

const UOMS = ['NOS', 'KG', 'LTR', 'BAG', 'BOX'];
// Catalogue/brochure languages (per master product, shared by all variants).
const CATALOGUE_LANGS: { code: string; label: string }[] = [
  { code: 'en', label: 'English' },
  { code: 'hi', label: 'हिंदी · Hindi' },
  { code: 'mr', label: 'मराठी · Marathi' },
  { code: 'or', label: 'ଓଡ଼ିଆ · Odia' },
  { code: 'bn', label: 'বাংলা · Bengali' },
  { code: 'gu', label: 'ગુજરાતી · Gujarati' },
];
// Wizard tab order + the required fields to validate before advancing past each tab.
const TAB_KEYS = ['basics', 'variants', 'media', 'details'];
const TAB_FIELDS: Record<string, string[]> = { basics: ['name', 'uomCode'], variants: [], media: [], details: [] };

type VariantRow = {
  key: string;
  sku: string;
  variantLabel: string;
  labelTouched?: boolean; // true once the user types a custom label
  mrp?: number;
  dealerPrice?: number;
  packSize?: number;
  moq?: number;
  imageUrl?: string;
  unitsPerBox?: number; // box packing: saleable units per box (a count, not derived from pack size/UoM)
};

// Short unit shown as the addon beside the pack-size NUMBER input (you type "0.25"
// next to "L"). The variant LABEL itself is rendered UOM-friendly via fmtUom below.
const UOM_SUFFIX: Record<string, string> = { NOS: 'Nos', KG: 'Kg', LTR: 'L', BAG: 'Bag', BOX: 'Box' };

// The variant label auto-derives from pack size + the PRODUCT UoM (chosen once in
// Basics, applied to every variant), UOM-friendly: 0.25 LTR → "250 ml", 1 → "1 L",
// 5 KG → "5 kg". Editable to override.
function variantLabelOf(r: VariantRow, uom: string): string {
  if (r.labelTouched) return r.variantLabel ?? '';
  if (r.packSize === undefined || r.packSize === null) return '';
  return fmtUom(r.packSize, uom);
}

function newVariantRow(): VariantRow {
  return { key: Math.random().toString(36).slice(2), sku: '', variantLabel: '', moq: 1, packSize: 1 };
}

export function ProductFormModal({ open, onClose, record }: Props) {
  const [form] = Form.useForm();
  const { message } = useApp();
  const qc = useQueryClient();
  const isEdit = !!record;
  // Shopify-style master/variant split:
  //  - 'master'  → edit shared fields once; they apply to EVERY variant in the group
  //                (+ the shared catalogue & product page).
  //  - 'variant' → edit only this pack (label, pack size, pricing, image, active).
  //  - 'full'    → a standalone product (no variants) — both at once (legacy behaviour).
  //  - 'create'  → the new-product wizard.
  const mode: 'create' | 'full' | 'master' | 'variant' = !record ? 'create' : (record._editMode ?? 'full');
  const isMaster = mode === 'master';
  const isVariant = mode === 'variant';
  // Every variant id in the group, so a master save propagates shared fields to all packs.
  const groupVariantIds: string[] = isMaster
    ? Array.from(new Set((record?.children ?? []).map((c: any) => String(c.id ?? c.productId)).filter(Boolean)))
    : [];
  // Changing the price of an existing product needs PRODUCT.PRICE.EDIT (the
  // backend enforces this too). New products set their initial price under
  // PRODUCT.CREATE, so the field stays editable while creating.
  const canEditPrice = usePermission('PRODUCT.PRICE.EDIT');
  const mrpLocked = isEdit && !canEditPrice;
  // The SKU is the product's business code, so renaming an existing one is gated (PRODUCT.SKU.EDIT —
  // Super Admin only by default) and enforced again by the backend. Everyone else sees it read-only.
  const canEditSku = usePermission('PRODUCT.SKU.EDIT');
  // Box-packing config is a gated capability (PRODUCT.PACKAGING.EDIT); unauthorized users see it read-only.
  const canEditPackaging = usePermission('PRODUCT.PACKAGING.EDIT');
  const boxEnabled = Form.useWatch('boxPackingEnabled', form);
  // Catalogue (multi-language) is a PRODUCT.EDIT capability on the master product.
  const canCatalogue = usePermission('PRODUCT.EDIT');
  const [catalogues, setCatalogues] = useState<Record<string, string>>({});
  const [details, setDetails] = useState<ProductDetailsState>({ highlights: [], resources: [], sections: [] });
  const [variants, setVariants] = useState<VariantRow[]>([newVariantRow()]);
  const watchedUom = Form.useWatch('uomCode', form);
  const defaultUom = (watchedUom as string) || 'NOS';
  // shelfLifeMonths === 0 ⇒ product never expires (non-perishable).
  const noExpiry = Form.useWatch('noExpiry', form);
  // A variant only "counts" (and will pass submit) when it has an SKU, a label
  // and a positive cost — so the "Create N SKUs" label matches what's accepted.
  const readyCount = variants.filter((v) => v.sku && variantLabelOf(v, defaultUom) && Number(v.dealerPrice) > 0).length;
  const [tab, setTab] = useState('basics');
  // Map a form field to the tab that holds it — so a validation error jumps to it.
  const fieldTab = (name: string) =>
    ['variantLabel', 'packSize', 'mrp', 'dealerPrice', 'moq', 'reorderLevel'].includes(name) ? 'variants'
      : ['imageUrls', 'videoUrl', 'videoUrls', 'brochureUrl'].includes(name) ? 'media' : 'basics';

  const { data: taxMasters } = useQuery({
    queryKey: ['tax-masters'],
    queryFn: () => request<any[]>({ url: '/tax-masters' }),
    enabled: open,
  });

  // Existing group codes for the Group code combobox. Typing a new value is
  // allowed (AutoComplete) and is persisted on save, so it appears next time.
  const { data: groupCodeSource } = useQuery({
    queryKey: ['products-group-codes'],
    queryFn: () => requestAllPages('/products'),
    enabled: open,
  });
  const groupCodeOptions = useMemo(() => {
    const set = new Set<string>();
    for (const p of groupCodeSource?.data ?? []) if (p.groupCode) set.add(p.groupCode);
    return Array.from(set).sort().map((v) => ({ value: v }));
  }, [groupCodeSource]);

  // The row handed in by the catalog list is keyed by `productId` and only
  // carries the columns the list shows — it has no `id` and is missing fields
  // like reorderLevel / shelfLifeMonths / hsnCode / description. Editing from
  // that partial row would (a) PATCH /products/undefined and (b) wipe the
  // missing fields on save. So when editing we re-fetch the full product and
  // populate the form from that.
  const editId = record?.id ?? record?.productId;
  const { data: fullProduct } = useQuery({
    queryKey: ['product-edit', editId],
    queryFn: () => request<any>({ url: `/products/${editId}` }),
    enabled: open && !!editId,
  });

  // Existing per-master catalogue files (shared by all variants of this product).
  const { data: cataloguesData } = useQuery({
    queryKey: ['product-catalogues', editId],
    queryFn: () => request<Record<string, string>>({ url: `/products/${editId}/catalogues` }),
    enabled: open && !!editId && canCatalogue,
  });
  useEffect(() => { if (open) setCatalogues(cataloguesData ?? {}); }, [open, cataloguesData]);

  // Existing rich catalogue details (per master, shared by all variants).
  const { data: detailsData } = useQuery({
    queryKey: ['product-details-edit', editId],
    queryFn: () => request<any>({ url: `/products/${editId}/details` }),
    enabled: open && !!editId && canCatalogue,
  });
  useEffect(() => {
    if (!open) return;
    setDetails({
      highlights: detailsData?.highlights ?? [],
      resources: detailsData?.resources ?? [],
      sections: detailsData?.sections ?? [],
      category: detailsData?.category ?? null,
      crops: detailsData?.crops ?? [],
      tagline: detailsData?.tagline ?? null,
      summary: detailsData?.summary ?? null,
    });
  }, [open, detailsData]);

  const openedRef = useRef(false);
  // Seed the form via `initialValues` + a remounting `key` (below) — NOT form.setFieldsValue() in an
  // effect. In this Radix-based ui.Modal, an effect fires before the portalled Form fields have mounted,
  // so setFieldsValue lands on nothing and the Edit form shows blank. (Codebase rule: seed edit modals
  // with initialValues + key, never setFieldsValue-in-effect.)
  const seedValues = useMemo(() => {
    if (!record) return { uomCode: 'NOS', packSize: 1, moq: 1, isActive: true, shelfLifeMonths: 24 };
    const src: any = fullProduct ?? record;
    return {
      ...src,
      mrp: Number(src.mrp ?? 0),
      dealerPrice: Number(src.dealerPrice ?? 0),
      moq: Number(src.moq ?? 1),
      packSize: Number(src.packSize ?? 1),
      reorderLevel: Number(src.reorderLevel ?? 0),
      shelfLifeMonths: Number(src.shelfLifeMonths) === 0 ? null : Number(src.shelfLifeMonths ?? 24),
      noExpiry: Number(src.shelfLifeMonths) === 0,
      isActive: src.isActive ?? true,
      imageUrls: toImageUrls(src.imageUrls).length ? toImageUrls(src.imageUrls) : (src.imageUrl ? [src.imageUrl] : []),
      videoUrls: toImageUrls(src.videoUrls).length ? toImageUrls(src.videoUrls) : (src.videoUrl ? [src.videoUrl] : []),
    };
  }, [record, fullProduct]);
  // Remount the <Form> when the record changes AND once the full record finishes loading, so the fresh
  // initialValues re-apply (changing initialValues alone never re-seeds an already-mounted form).
  const formKey = record ? `edit-${String(editId)}-${fullProduct ? 'full' : 'partial'}` : 'create';

  useEffect(() => {
    if (!open) { openedRef.current = false; return; }
    if (openedRef.current) return;
    openedRef.current = true;
    // Wizard scaffolding only (variant rows live outside the form). Variant edits open on the Pricing tab.
    setVariants([newVariantRow()]);
    setTab(isVariant ? 'variants' : 'basics');
  }, [open, isVariant]);

  const save = useConfirmedMutation({ title: "Save this product?", content: "It becomes orderable as soon as it is saved.", okText: "Yes, save" }, {
    mutationFn: async (vals: any) => {
      let resp: any;
      if (!isEdit) {
        // Validate variant rows
        const rows = variants
          .map((v) => ({
            sku: (v.sku ?? '').trim(),
            variantLabel: variantLabelOf(v, defaultUom).trim(),
            mrp: Number(v.mrp ?? vals.mrp ?? 0),
            dealerPrice: Number(v.dealerPrice ?? vals.dealerPrice ?? 0),
            packSize: Number(v.packSize ?? vals.packSize ?? 1),
            moq: Number(v.moq ?? vals.moq ?? 1),
            imageUrl: v.imageUrl,
            unitsPerBox: Number(v.unitsPerBox) > 0 ? Math.floor(Number(v.unitsPerBox)) : undefined,
          }))
          .filter((v) => v.sku && v.variantLabel);
        if (!rows.length) {
          throw new Error('Add at least one variant row with SKU and label');
        }
        if (rows.some((r) => !(r.dealerPrice > 0))) {
          throw new Error('Each variant needs a dealer price greater than 0');
        }
        resp = await request({
          url: '/products/with-variants',
          method: 'POST',
          data: {
            name: vals.name,
            groupCode: vals.groupCode,
            hsnCode: vals.hsnCode,
            uomCode: vals.uomCode,
            // noExpiry ⇒ shelfLifeMonths 0 (never expires).
            shelfLifeMonths: vals.noExpiry ? '0' : (vals.shelfLifeMonths ? String(vals.shelfLifeMonths) : undefined),
            imageUrls: vals.imageUrls,
            videoUrls: vals.videoUrls,
            isActive: vals.isActive ?? true,
            variants: rows.map((r) => ({
              sku: r.sku,
              variantLabel: r.variantLabel,
              mrp: String(r.mrp),
              dealerPrice: String(r.dealerPrice ?? 0),
              packSize: String(r.packSize),
              moq: String(r.moq),
              imageUrl: r.imageUrl,
              // Box packing set during creation — only sent by an authorized editor.
              ...(canEditPackaging && r.unitsPerBox && r.unitsPerBox > 0 ? { boxPackingEnabled: true, unitsPerBox: r.unitsPerBox } : {}),
            })),
          },
        });
      } else if (isVariant) {
        // Variant edit — this pack only. Per-variant fields, no shared content touched.
        return request({
          url: `/products/${editId}`,
          method: 'PATCH',
          data: {
            // The variant payload names its fields, so an edited SKU would be dropped here without this.
            // Only an authorized editor sends it; an unchanged value is a no-op for the backend gate.
            ...(canEditSku ? { sku: vals.sku } : {}),
            variantLabel: vals.variantLabel,
            packSize: String(vals.packSize ?? 1),
            mrp: String(vals.mrp ?? 0),
            dealerPrice: String(vals.dealerPrice ?? 0),
            moq: String(vals.moq ?? 1),
            imageUrls: vals.imageUrls,
            isActive: vals.isActive ?? true,
            // Box packing — only sent by an authorized editor (else the backend gate would 403 an untouched edit).
            ...(canEditPackaging ? {
              boxPackingEnabled: !!vals.boxPackingEnabled,
              unitsPerBox: vals.boxPackingEnabled ? Number(vals.unitsPerBox) : null,
              boxBarcode: vals.boxBarcode || null,
              boxSku: vals.boxSku || null,
            } : {}),
          },
        });
      } else if (isMaster) {
        // Master edit — shared basics applied to EVERY variant in the group, plus the
        // group-keyed catalogue + product page (saved once via a representative id).
        const shared = {
          name: vals.name,
          groupCode: vals.groupCode,
          hsnCode: vals.hsnCode,
          uomCode: vals.uomCode,
          videoUrls: vals.videoUrls,
          shelfLifeMonths: vals.noExpiry ? 0 : vals.shelfLifeMonths,
        };
        const ids = groupVariantIds.length ? groupVariantIds : [editId];
        await Promise.all(ids.map((id) => request({ url: `/products/${id}`, method: 'PATCH', data: shared })));
        if (canCatalogue) {
          await request({ url: `/products/${editId}/catalogues`, method: 'PUT', data: catalogues });
          await request({ url: `/products/${editId}/details`, method: 'PUT', data: details });
        }
        return { id: editId };
      } else {
        const payload: any = {
          ...vals,
          mrp: String(vals.mrp ?? 0),
          dealerPrice: String(vals.dealerPrice ?? 0),
          moq: String(vals.moq ?? 1),
          packSize: String(vals.packSize ?? 1),
          // noExpiry ⇒ shelfLifeMonths 0 (never expires).
          shelfLifeMonths: vals.noExpiry ? 0 : vals.shelfLifeMonths,
        };
        delete payload.noExpiry; // UI-only helper, not a product field
        // Box packing: authorized editors send a normalised pair; everyone else must NOT send packaging keys
        // (a seeded spread would otherwise trip the backend PRODUCT.PACKAGING.EDIT gate on an untouched edit).
        if (canEditPackaging) {
          payload.boxPackingEnabled = !!vals.boxPackingEnabled;
          payload.unitsPerBox = vals.boxPackingEnabled ? Number(vals.unitsPerBox) : null;
          payload.boxBarcode = vals.boxBarcode || null;
          payload.boxSku = vals.boxSku || null;
        } else {
          for (const k of ['boxPackingEnabled', 'unitsPerBox', 'boxBarcode', 'boxSku', 'boxLabel', 'boxWeight', 'boxLength', 'boxWidth', 'boxHeight']) delete payload[k];
        }
        resp = await request({ url: `/products/${editId}`, method: 'PATCH', data: payload });
      }
      // Persist the per-master multi-language catalogue (shared by all variants).
      if (canCatalogue) {
        const pid = isEdit ? editId : (Array.isArray(resp) ? resp[0]?.id : resp?.id);
        if (pid && (isEdit || Object.values(catalogues).some(Boolean))) {
          await request({ url: `/products/${pid}/catalogues`, method: 'PUT', data: catalogues });
        }
        // Persist the rich catalogue page (highlights / resources / sections), per master.
        const hasDetails = details.highlights.length || details.resources.length || details.sections.length;
        if (pid && (isEdit || hasDetails)) {
          await request({ url: `/products/${pid}/details`, method: 'PUT', data: details });
        }
      }
      return resp;
    },
    onSuccess: (result: any) => {
      if (!isEdit) {
        const n = Array.isArray(result) ? result.length : 1;
        message.success(`Created ${n} variant${n === 1 ? '' : 's'}`);
      } else {
        message.success(isMaster ? 'Product updated for all variants' : isVariant ? 'Variant updated' : 'Product updated');
      }
      qc.invalidateQueries({ queryKey: ['catalog-with-stock'] });
      qc.invalidateQueries({ queryKey: ['catalog-facets'] });
      qc.invalidateQueries({ queryKey: ['products-list'] });
      qc.invalidateQueries({ queryKey: ['product-catalogues'] });
      qc.invalidateQueries({ queryKey: ['product-details-edit'] });
      qc.invalidateQueries({ queryKey: ['shop-details'] });
      qc.invalidateQueries({ queryKey: ['shop-all-products'] });
      onClose();
    },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? e.message ?? 'Failed'),
  });

  // Don't discard a half-filled form on a stray click. `maskClosable={false}` (below) already blocks the
  // backdrop click that started this; this also guards the ✕ / Esc / Cancel when there's unsaved input.
  const isDirty = () => {
    if (form.isFieldsTouched()) return true;
    if (!isEdit) {
      // New-product wizard: variants + catalogues + details start empty, so ANY content is unsaved input.
      if (variants.some((v) => v.sku || v.variantLabel || v.mrp || v.dealerPrice || v.imageUrl || v.unitsPerBox)) return true;
      if (Object.values(catalogues).some(Boolean)) return true;
      if (details.highlights.length || details.resources.length || details.sections.length) return true;
    }
    return false;
  };
  const requestClose = () => {
    if (save.isPending) return; // never interrupt an in-flight save
    if (!isDirty()) { onClose(); return; }
    Modal.confirm({
      title: 'Discard unsaved changes?',
      content: 'You’ve started filling this product. If you close now, the entered details will be lost.',
      okText: 'Discard', okButtonProps: { danger: true },
      cancelText: 'Keep editing',
      onOk: onClose,
    });
  };

  const updateVariant = (key: string, patch: Partial<VariantRow>) =>
    setVariants((arr) => arr.map((v) => (v.key === key ? { ...v, ...patch } : v)));

  const removeVariant = (key: string) =>
    setVariants((arr) => (arr.length > 1 ? arr.filter((v) => v.key !== key) : arr));

  // Read-only unless the editor may rename it (see canEditSku). The hint names the cost of a rename:
  // the SKU is what import sheets and printed labels quote.
  const skuLocked = isEdit && !canEditSku;
  const skuField = (
    <Col span={6}>
      <Form.Item
        name="sku"
        label={isEdit ? 'SKU' : 'Parent SKU prefix (optional)'}
        rules={isEdit ? [{ required: true }] : []}
        extra={
          !isEdit ? 'Used to auto-derive the group code'
            : skuLocked ? 'Needs PRODUCT.SKU.EDIT to change'
            : 'The code on labels and import sheets — old sheets stop matching once it changes'
        }
      >
        <Input placeholder={isEdit ? 'GBB-XXX-1L' : 'GBB-NPK'} disabled={skuLocked} maxLength={40} />
      </Form.Item>
    </Col>
  );

  // Footer: a Next → Next → Create wizard for new products (Create only on the
  // last tab); a simple Update for edits (save from any tab). Next validates the
  // current tab's required fields before advancing.
  const tabIndex = TAB_KEYS.indexOf(tab);
  const isLastTab = tabIndex === TAB_KEYS.length - 1;
  const goNext = () => {
    // The variant rows live outside AntD form state, so validateFields can't see
    // them — gate the variants tab on at least one complete (SKU + label + cost) row.
    if (tab === 'variants' && readyCount === 0) {
      message.warning('Add at least one variant with an SKU, label and cost before continuing.');
      return;
    }
    form.validateFields(TAB_FIELDS[tab] ?? [])
      .then(() => { if (tabIndex < TAB_KEYS.length - 1) setTab(TAB_KEYS[tabIndex + 1]); })
      .catch(() => { /* errors shown inline on the field */ });
  };
  const createLabel = readyCount ? `Create ${readyCount} ${readyCount === 1 ? 'product' : 'SKUs'}` : 'Create';
  const footer = isEdit
    ? [
        <Button key="cancel" variant="default" onClick={requestClose}>Cancel</Button>,
        <Button key="save" variant="primary" loading={save.isPending} onClick={() => form.submit()}>Update</Button>,
      ]
    : [
        tabIndex > 0 ? <Button key="back" variant="default" onClick={() => setTab(TAB_KEYS[tabIndex - 1])}>Back</Button> : null,
        <Button key="cancel" variant="default" onClick={requestClose}>Cancel</Button>,
        isLastTab
          ? <Button key="create" variant="primary" loading={save.isPending} onClick={() => form.submit()}>{createLabel}</Button>
          : <Button key="next" variant="primary" onClick={goNext}>Next</Button>,
      ];

  return (
    <Modal
      open={open}
      maskClosable={false}
      onOpenChange={(o) => { if (!o) requestClose(); }}
      title={
        !isEdit ? 'New product'
          : isMaster ? `Edit product · ${record?.name || record?.groupCode || record?.sku} (all variants)`
          : isVariant ? `Edit variant · ${record?.variantLabel || record?.sku}`
          : `Edit · ${record?.sku}`
      }
      width={1080}
      footer={footer}
    >
      <Form
        key={formKey}
        form={form}
        layout="vertical"
        initialValues={seedValues}
        onFinish={(v) => save.mutate(v)}
        onFinishFailed={({ errorFields }) => {
          const first = errorFields?.[0]?.name?.[0] as string | undefined;
          if (first) setTab(fieldTab(first));
          message.warning('Please complete the highlighted required fields.');
        }}
      >
        <Tabs
          activeKey={tab}
          onChange={setTab}
          items={[
            (mode !== 'variant') && {
              key: 'basics',
              label: 'Basics',
              forceRender: true,
              children: (
                <Row gutter={16}>
                  {!isMaster && skuField}
                  <Col span={isMaster ? 18 : 12}>
                    <Form.Item name="name" label="Product Name" rules={[{ required: true }]}>
                      <Input placeholder="e.g. GreenGrow NPK" />
                    </Form.Item>
                  </Col>
                  {!isMaster && (
                    <Col span={6}>
                      <Form.Item name="isActive" label="Active" valuePropName="checked">
                        <Switch checkedChildren="ON" unCheckedChildren="OFF" />
                      </Form.Item>
                    </Col>
                  )}
                  <Col span={8}>
                    <Form.Item
                      name="groupCode"
                      label="Group code"
                      extra="Optional — auto-derived from the parent SKU if blank. Applied to every variant."
                    >
                      <AutoComplete
                        options={groupCodeOptions}
                        placeholder="Pick an existing code or type a new one"
                        allowClear
                        filterOption={(input, option) =>
                          String(option?.value ?? '').toUpperCase().includes(input.toUpperCase())
                        }
                      />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item name="hsnCode" label="HSN code">
                      <Select
                        allowClear
                        showSearch
                        optionFilterProp="label"
                        placeholder="HSN"
                        options={(taxMasters ?? []).map((t: any) => ({
                          value: t.hsnCode,
                          label: `${t.hsnCode} · ${t.description}`,
                        }))}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item name="uomCode" label="UoM" rules={[{ required: true }]} extra="Applies to every variant; the variant label uses it.">
                      <Select options={UOMS.map((u) => ({ value: u, label: u }))} />
                    </Form.Item>
                  </Col>
                  <Col span={8}>
                    <Form.Item
                      label="Shelf life (months)"
                      extra={
                        // A plain small toggle with the label BESIDE it — cramming "No expiry" inside a
                        // small switch's fixed track clipped the text into a broken pill.
                        <span className="inline-flex items-center gap-2">
                          <Form.Item name="noExpiry" valuePropName="checked" noStyle>
                            <Switch size="small" />
                          </Form.Item>
                          No expiry
                        </span>
                      }
                    >
                      <Form.Item name="shelfLifeMonths" noStyle>
                        <InputNumber
                          style={{ width: '100%' }}
                          min={1}
                          step={1}
                          disabled={!!noExpiry}
                          placeholder={noExpiry ? 'Never expires' : undefined}
                        />
                      </Form.Item>
                    </Form.Item>
                  </Col>
                  <Col span={16}>
                    <div className="pt-1 text-sm text-muted">
                      Product <b>description</b> &amp; full details are managed on the <b>Product page</b> tab —
                      they’re shared across all variants (1&nbsp;L, 2&nbsp;L…).
                    </div>
                  </Col>
                </Row>
              ),
            },
            (mode !== 'master') && {
              key: 'variants',
              label: isEdit ? 'Pricing' : `Variants & Pricing${readyCount ? ` · ${readyCount}` : ''}`,
              forceRender: true,
              children: (
                <Row gutter={16}>
                  {isEdit ? (
                    <>
                      {/* A pack carries its OWN SKU, and the variant modal has no Basics tab — so the
                          field belongs here, beside the label that identifies the same pack. */}
                      {isVariant && skuField}
                      <Col span={6}>
                        <Form.Item name="variantLabel" label="Variant label">
                          <Input placeholder="1 L · 5 L · 25 Kg" />
                        </Form.Item>
                      </Col>
                      <Col span={6}>
                        <Form.Item name="packSize" label="Pack size">
                          <InputNumber style={{ width: '100%' }} min={0.001} step={0.5} />
                        </Form.Item>
                      </Col>
                      <Col span={6}>
                        <Form.Item
                          name="mrp"
                          label="MRP (₹)"
                          rules={[{ required: true }]}
                          extra={mrpLocked ? 'Needs PRODUCT.PRICE.EDIT to change' : undefined}
                        >
                          <InputNumber style={{ width: '100%' }} min={0} step={10} disabled={mrpLocked} />
                        </Form.Item>
                      </Col>
                      <Col span={6}>
                        <Form.Item name="dealerPrice" label="Dealer price (₹)" extra="Billed to the dealer on orders & invoices"
                          rules={[{ required: true, message: 'Dealer price is required' }, { type: 'number', min: 0.01, message: 'Must be greater than 0' }]}>
                          <InputNumber style={{ width: '100%' }} min={0} step={10} />
                        </Form.Item>
                      </Col>
                      <Col span={6}>
                        <Form.Item name="moq" label="MOQ">
                          <InputNumber style={{ width: '100%' }} min={0.001} step={1} />
                        </Form.Item>
                      </Col>
                      {isVariant && (
                        <Col span={6}>
                          <Form.Item name="isActive" label="Active" valuePropName="checked">
                            <Switch checkedChildren="ON" unCheckedChildren="OFF" />
                          </Form.Item>
                        </Col>
                      )}

                      {/* ── Box packing (per variant) — how many saleable UNITS are in one physical box.
                             A manual count, NOT derived from the variant size/UoM. Gated by PRODUCT.PACKAGING.EDIT. ── */}
                      <Col span={24}>
                        <Divider orientation="left" style={{ margin: '4px 0 12px' }}>
                          <span style={{ fontSize: 13 }}>Box packing</span>
                        </Divider>
                      </Col>
                      <Col span={6}>
                        <Form.Item name="boxPackingEnabled" label="Box packing" valuePropName="checked" extra="Sold in boxes">
                          <Switch checkedChildren="ON" unCheckedChildren="OFF" disabled={!canEditPackaging} />
                        </Form.Item>
                      </Col>
                      <Col span={6}>
                        <Form.Item
                          name="unitsPerBox"
                          label="Units per box"
                          extra="Saleable units in 1 box (count)"
                          rules={boxEnabled ? [
                            { required: true, message: 'Required when box packing is on' },
                            { type: 'integer', min: 1, message: 'Whole number greater than 0' },
                          ] : []}
                        >
                          <InputNumber style={{ width: '100%' }} min={1} step={1} precision={0} placeholder="e.g. 10" disabled={!canEditPackaging || !boxEnabled} />
                        </Form.Item>
                      </Col>
                      <Col span={6}>
                        <Form.Item name="boxBarcode" label="Box barcode" extra="Optional">
                          <Input disabled={!canEditPackaging || !boxEnabled} placeholder="Scan / type" />
                        </Form.Item>
                      </Col>
                      <Col span={6}>
                        <Form.Item name="boxSku" label="Box SKU / code" extra="Optional">
                          <Input disabled={!canEditPackaging || !boxEnabled} placeholder="e.g. AGN-500-BOX" />
                        </Form.Item>
                      </Col>
                    </>
                  ) : (
                    <Col span={24}>
                      <Alert
                        type="info"
                        showIcon
                        icon={<Lightbulb size={16} />}
                        style={{ marginBottom: 8 }}
                        message="Add one row per pack/size — a product with a single pack is just one row."
                        description="The label auto-fills from pack size + the product UoM set in Basics (e.g. 1 → 1L) — edit it to override. All rows share this product's Basics & Media."
                      />
                      <VariantsEditor
                        rows={variants}
                        defaultUom={defaultUom}
                        canEditPackaging={canEditPackaging}
                        onUpdate={updateVariant}
                        onRemove={removeVariant}
                        onAdd={() => setVariants((arr) => [...arr, newVariantRow()])}
                      />
                    </Col>
                  )}
                </Row>
              ),
            },
            {
              key: 'media',
              label: 'Media & Catalogue',
              forceRender: true,
              children: (
                <Row gutter={16}>
                  {!isMaster && (
                    <Col span={24}>
                      <Form.Item
                        name="imageUrls"
                        label={isVariant ? 'Variant images' : 'Product images'}
                        extra={isVariant
                          ? 'Photos for this pack — the first is the list thumbnail.'
                          : 'Add one or more photos — all show in the gallery. Tap “Cover” on any photo to make it the list thumbnail. A variant row\'s own image overrides this.'}
                      >
                        <MultiImageUploadField />
                      </Form.Item>
                    </Col>
                  )}
                  {!isVariant && (
                    <Col span={12}>
                      <Form.Item name="videoUrls" label="Demo videos" extra="Add one or more product / demo videos.">
                        <MultiFileUploadField category="product-video" buttonLabel="Upload videos" />
                      </Form.Item>
                    </Col>
                  )}
                  {!isVariant && canCatalogue && (
                    <Col span={24}>
                      <Divider orientation="left" style={{ margin: '4px 0 12px' }}>Catalogue by language</Divider>
                      <Alert
                        type="info" showIcon style={{ marginBottom: 12 }}
                        message="One catalogue per language for this product — shared by all its variants."
                      />
                      <Row gutter={[16, 8]}>
                        {CATALOGUE_LANGS.map((l) => (
                          <Col xs={24} md={12} key={l.code}>
                            <div style={{ fontSize: 12, marginBottom: 4 }}>{l.label}</div>
                            <FileUploadField
                              category="product-brochure"
                              buttonLabel={`Upload ${l.label.split(' · ')[0]} PDF`}
                              preview={false}
                              value={catalogues[l.code]}
                              onChange={(url) => setCatalogues((c) => ({ ...c, [l.code]: url }))}
                            />
                          </Col>
                        ))}
                      </Row>
                    </Col>
                  )}
                </Row>
              ),
            },
            (mode !== 'variant') && {
              key: 'details',
              label: 'Product page',
              forceRender: true,
              children: canCatalogue ? (
                <ProductDetailsEditor value={details} onChange={setDetails} />
              ) : (
                <Alert type="info" showIcon message="You need PRODUCT.EDIT to edit the product page content." />
              ),
            },
          ].filter(Boolean) as any}
        />
      </Form>
    </Modal>
  );
}

function VariantsEditor({
  rows, onUpdate, onRemove, onAdd, defaultUom, canEditPackaging,
}: {
  rows: VariantRow[];
  onUpdate: (key: string, patch: Partial<VariantRow>) => void;
  onRemove: (key: string) => void;
  onAdd: () => void;
  defaultUom: string;
  canEditPackaging: boolean;
}) {
  return (
    <>
      <Table
        size="small"
        rowKey="key"
        dataSource={rows}
        pagination={false}
        scroll={{ x: canEditPackaging ? 1020 : 920 }}
        columns={[
          {
            title: 'SKU', width: 150,
            render: (_: any, r: VariantRow) => (
              <Input
                size="small"
                value={r.sku}
                placeholder="GBB-NPK-1L"
                onChange={(e) => onUpdate(r.key, { sku: e.target.value })}
              />
            ),
          },
          {
            title: 'Pack size', width: 140,
            render: (_: any, r: VariantRow) => (
              <InputNumber
                size="small"
                style={{ width: '100%' }}
                min={0.001}
                step={0.5}
                value={r.packSize}
                addonAfter={UOM_SUFFIX[defaultUom] ?? defaultUom}
                onChange={(v) => onUpdate(r.key, { packSize: Number(v ?? 0) })}
              />
            ),
          },
          {
            title: 'Variant label', width: 100,
            render: (_: any, r: VariantRow) => (
              <Tag style={{ fontSize: 12, margin: 0 }}>{variantLabelOf(r, defaultUom) || '—'}</Tag>
            ),
          },
          {
            title: 'MRP ₹', width: 100,
            render: (_: any, r: VariantRow) => (
              <InputNumber
                size="small"
                style={{ width: '100%' }}
                min={0}
                step={10}
                value={r.mrp}
                onChange={(v) => onUpdate(r.key, { mrp: Number(v ?? 0) })}
              />
            ),
          },
          {
            title: <span>Dealer ₹ <span style={{ color: PALETTE.danger[500] }}>*</span></span>, width: 100,
            render: (_: any, r: VariantRow) => (
              <InputNumber
                size="small"
                style={{ width: '100%' }}
                min={0}
                step={10}
                value={r.dealerPrice}
                status={!(Number(r.dealerPrice) > 0) ? 'error' : undefined}
                onChange={(v) => onUpdate(r.key, { dealerPrice: Number(v ?? 0) })}
              />
            ),
          },
          {
            title: 'MOQ', width: 80,
            render: (_: any, r: VariantRow) => (
              <InputNumber
                size="small"
                style={{ width: '100%' }}
                min={0.001}
                step={1}
                value={r.moq}
                onChange={(v) => onUpdate(r.key, { moq: Number(v ?? 0) })}
              />
            ),
          },
          // Box packing (per variant): saleable units in one box. A count — NOT derived from the pack size/UoM.
          ...(canEditPackaging ? [{
            title: <Tooltip title="How many saleable units are in one box (a count, not derived from the size). Leave blank for no box packing."><span>Units/box</span></Tooltip>,
            width: 110,
            render: (_: any, r: VariantRow) => (
              <InputNumber
                size="small"
                style={{ width: '100%' }}
                min={1}
                step={1}
                precision={0}
                placeholder="e.g. 10"
                value={r.unitsPerBox}
                onChange={(v) => onUpdate(r.key, { unitsPerBox: v == null ? undefined : Math.max(0, Math.floor(Number(v))) })}
              />
            ),
          }] : []),
          {
            title: 'Image override', width: 230,
            render: (_: any, r: VariantRow) => (
              <FileUploadField
                category="product-image"
                size="small"
                preview={false}
                buttonLabel={r.imageUrl ? 'Replace' : 'Upload'}
                value={r.imageUrl}
                onChange={(url) => onUpdate(r.key, { imageUrl: url })}
              />
            ),
          },
          {
            title: '', width: 50,
            render: (_: any, r: VariantRow) => (
              <Tooltip title={rows.length > 1 ? 'Remove row' : 'Keep at least one row'}>
                <Button
                  size="sm"
                  variant="danger"
                  icon={<Trash2 size={16} />}
                  onClick={() => onRemove(r.key)}
                  disabled={rows.length <= 1}
                  aria-label="Remove row"
                />
              </Tooltip>
            ),
          },
        ]}
      />
      <Space style={{ marginTop: 8 }}>
        <Button variant="default" icon={<Plus size={16} />} onClick={onAdd}>Add variant row</Button>
        <Tag>{rows.filter((r) => r.sku && variantLabelOf(r, defaultUom)).length} ready</Tag>
      </Space>
    </>
  );
}
